/*
 * Save for later. Pieces a visitor saves are kept on their device
 * (localStorage, key "tah-saved", newest first), with no app and no account.
 *
 *   <tah-save-button>   the Save for later link or bookmark mark
 *   <tah-saved-count>   the number beside the header bookmark
 *   <tah-saved-list>    the Saved page, which fetches each piece as a card
 *                       from its alternate template (?view=tah-card)
 */
(() => {
  const KEY = 'tah-saved';
  const MAX = 100;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
    } catch {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch {
      /* private browsing: the change still shows on this page */
    }
    document.dispatchEvent(new CustomEvent('tah:saved', { detail: { list } }));
  };

  const has = (handle) => read().includes(handle);

  const toggle = (handle) => {
    const list = read();
    const at = list.indexOf(handle);
    if (at === -1) list.unshift(handle);
    else list.splice(at, 1);
    write(list);
    return at === -1;
  };

  const remove = (handle) => write(read().filter((h) => h !== handle));

  window.TahSaved = { read, has, toggle, remove };

  /* Keep other open tabs in step. */
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) document.dispatchEvent(new CustomEvent('tah:saved', { detail: { list: read() } }));
  });

  class TahSaveButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-save-label]');
      if (!this.handle || !this.button) return;
      this.button.addEventListener('click', (event) => {
        /* Cards are one big link; the mark must not open the piece. */
        event.preventDefault();
        event.stopPropagation();
        this.show(toggle(this.handle));
      });
      this.sync = () => this.show(has(this.handle));
      document.addEventListener('tah:saved', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      if (this.sync) document.removeEventListener('tah:saved', this.sync);
    }

    show(saved) {
      this.classList.toggle('is-saved', saved);
      this.button.setAttribute('aria-pressed', String(saved));
      const text = saved ? this.dataset.savedText : this.dataset.saveText;
      if (this.label) this.label.textContent = text;
      this.button.title = text;
    }
  }

  class TahSavedCount extends HTMLElement {
    connectedCallback() {
      this.link = this.closest('[data-saved-link]');
      this.sync = () => {
        const count = read().length;
        this.textContent = count ? String(count) : '';
        if (this.link) {
          this.link.classList.toggle('has-saved', count > 0);
          this.link.setAttribute('aria-label', count ? `Saved pieces (${count})` : 'Saved pieces');
        }
      };
      document.addEventListener('tah:saved', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('tah:saved', this.sync);
    }
  }

  class TahSavedList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-saved-grid]');
      this.empty = this.querySelector('[data-saved-empty]');
      this.footer = this.querySelector('[data-saved-footer]');
      this.enquire = this.querySelector('[data-saved-enquire]');
      this.cache = new Map();

      this.grid.addEventListener('click', (event) => {
        const button = event.target.closest('[data-saved-remove]');
        if (!button) return;
        event.preventDefault();
        const card = button.closest('[data-handle]');
        if (card) card.classList.add('is-leaving');
        setTimeout(() => remove(button.dataset.savedRemove), card ? 300 : 0);
      });

      this.sync = () => this.render();
      document.addEventListener('tah:saved', this.sync);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('tah:saved', this.sync);
    }

    async card(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      let html = null;
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=tah-card`);
        if (response.ok) html = (await response.text()).trim();
      } catch {
        html = null;
      }
      this.cache.set(handle, html);
      return html;
    }

    async render() {
      const list = read();
      this.empty.hidden = list.length > 0;
      this.footer.hidden = list.length === 0;
      if (!list.length) {
        this.grid.innerHTML = '';
        return;
      }
      const cards = await Promise.all(list.map((handle) => this.card(handle)));
      this.grid.innerHTML = cards.filter(Boolean).join('');

      if (this.enquire) {
        const titles = [...this.grid.querySelectorAll('[data-title]')].map((el) => el.dataset.title);
        const base = this.enquire.dataset.base;
        this.enquire.href = `${base}?topic=piece&piece=${encodeURIComponent(titles.join('; '))}`;
      }
    }
  }

  if (!customElements.get('tah-save-button')) customElements.define('tah-save-button', TahSaveButton);
  if (!customElements.get('tah-saved-count')) customElements.define('tah-saved-count', TahSavedCount);
  if (!customElements.get('tah-saved-list')) customElements.define('tah-saved-list', TahSavedList);
})();
